function appTypeSlug(a) {
    if (!a || typeof a !== 'object') return '';
    if (a.type && a.type.slug) return a.type.slug;
    if (a.app_type && a.app_type.slug) return a.app_type.slug;
    if (typeof a.app_type === 'string') return a.app_type;
    return '';
}

function pickIndoorOrFirst(list) {
    if (!list || !list.length) return null;
    const indoor = list.find(a => appTypeSlug(a) === 'indoor_delivery');
    return indoor || list[0];
}

/**
 * IDs ativos em robot.settings.applications (objeto { [appId]: true }).
 * Não exige que o ID exista na lista rc3/applications — o GET applications/:id funciona mesmo assim.
 */
export function applicationIdsFromRobotSettings(robot) {
    const rApps = robot && robot.settings && robot.settings.applications;
    if (!rApps || typeof rApps !== 'object') {
        return [];
    }
    return Object.keys(rApps).filter(id => rApps[id]);
}

/**
 * Resolve qual aplicação usar (dados já em memória: status + lista RC3 + robô).
 * Ordem: status em tempo real → settings.applications do robô → apps na lista que referenciam o robô.
 */
export function resolveApplicationIdForRobot({ status, applications, robot }) {
    if (status && status.application && status.application.id) {
        return status.application.id;
    }

    if (!robot || !robot.id) {
        return null;
    }

    const fromSettings = applicationIdsFromRobotSettings(robot);
    if (fromSettings.length === 1) {
        return fromSettings[0];
    }
    if (fromSettings.length > 1 && applications && applications.length) {
        const metas = fromSettings
            .map(id => applications.find(a => a.id === id))
            .filter(Boolean);
        const chosen = pickIndoorOrFirst(metas);
        if (chosen) {
            return chosen.id;
        }
    }
    if (fromSettings.length > 0) {
        return fromSettings[0];
    }

    if (!applications || !applications.length) {
        return null;
    }

    const linked = applications.filter(
        a =>
            a.robots &&
            Array.isArray(a.robots) &&
            a.robots.some(r => r.id === robot.id)
    );

    if (linked.length === 1) {
        return linked[0].id;
    }
    if (linked.length > 1) {
        const chosen = pickIndoorOrFirst(linked);
        return chosen ? chosen.id : null;
    }

    return null;
}

/**
 * Resolve ID da aplicação para carregar mapa/pontos, com fallbacks em API
 * (robô completo e lista por tipo — mesmo padrão do formulário de robô na plataforma).
 */
export async function resolveApplicationIdForDeliveryLoad({
    api,
    status,
    applications,
    robot,
}) {
    let appId = resolveApplicationIdForRobot({
        status,
        applications,
        robot,
    });
    if (appId) {
        return appId;
    }

    if (!robot || !robot.id) {
        return null;
    }

    try {
        const { data: fullRobot } = await api.get(`robots/${robot.id}`);
        const merged = {
            ...robot,
            ...fullRobot,
            settings: {
                ...(robot.settings || {}),
                ...(fullRobot && fullRobot.settings ? fullRobot.settings : {}),
            },
        };
        appId = resolveApplicationIdForRobot({
            status,
            applications,
            robot: merged,
        });
        if (appId) {
            return appId;
        }
    } catch (_e) {
        /* ignorar: segue para applications?robot= */
    }

    const typeId = robot.type && robot.type.id ? robot.type.id : null;
    const groupId = robot.group && robot.group.id ? robot.group.id : null;
    if (!typeId || !groupId) {
        return null;
    }

    try {
        const { data } = await api.get(`applications?robot=${typeId}`);
        const list = (data || []).filter(
            a => a.group && a.group.id === groupId
        );
        const chosen = pickIndoorOrFirst(list);
        return chosen ? chosen.id : null;
    } catch (_e) {
        return null;
    }
}

/** Mesma convenção do MissionCard: settings.map_navigation[robotTypeSlug]. */
export function getMapNavigationEntry(applicationSettings, robotTypeCode) {
    if (!applicationSettings || !robotTypeCode) {
        return {};
    }
    const mapNav = applicationSettings.map_navigation || {};
    const byCode =
        mapNav[robotTypeCode] ||
        mapNav[robotTypeCode.toLowerCase()] ||
        mapNav[robotTypeCode.toUpperCase()];
    if (byCode && typeof byCode === 'object') {
        return byCode;
    }
    const keys = Object.keys(mapNav);
    if (keys.length === 1 && mapNav[keys[0]]) {
        return mapNav[keys[0]];
    }
    return {};
}

/** Normaliza ponto da API `map_points` para uso nos botões de entrega. */
export function normalizeMapPointForDelivery(point) {
    if (!point || typeof point !== 'object') {
        return null;
    }
    return {
        ...point,
        name: point.name || point.reference || '---',
        type: point.type || 'map_point',
    };
}
